import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Network,
  Play,
  Search,
  ShieldCheck,
  Key,
  Globe,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileText,
  FileCode,
  FileJson,
  History,
  Trash2,
} from 'lucide-react';
import clsx from 'clsx';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import './styles.css';

const FEATURES = [
  { id: 'jwt', name: 'JWT Decoder', icon: ShieldCheck, category: 'Authentication', status: 'active', url: chrome.runtime?.getURL ? chrome.runtime.getURL('jwt.html') : 'jwt.html' },
  { id: 'saml', name: 'SAML Inspector', icon: Key, category: 'Authentication', status: 'active', url: chrome.runtime?.getURL ? chrome.runtime.getURL('saml.html') : 'saml.html' },
  { id: 'rest', name: 'REST API Tester', icon: Network, category: 'API Testing', status: 'active', url: chrome.runtime?.getURL ? chrome.runtime.getURL('rest.html') : 'rest.html' },
  { id: 'har', name: 'HAR Analyzer', icon: FileCode, category: 'Debugging', status: 'active', url: chrome.runtime?.getURL ? chrome.runtime.getURL('har-analyzer.html') : 'har-analyzer.html' },
  { id: 'json', name: 'JSON Utility', icon: FileJson, category: 'Utilities', status: 'active', url: chrome.runtime?.getURL ? chrome.runtime.getURL('json-utility.html') : 'json-utility.html' },
  // Browser Logs temporarily disabled - not ready for production
  // { id: 'logs', name: 'Browser Logs', icon: FileText, category: 'Debugging', status: 'active', url: chrome.runtime?.getURL ? chrome.runtime.getURL('logs.html') : 'logs.html' },
];

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const AUTH_TYPES = ['None', 'Basic', 'Bearer Token', 'OAuth 2.0'];

const copyText = async (text) => {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    console.error('Copy failed', err);
  }
};

const RestTester = () => {
  const [url, setUrl] = useState('');
  const [method, setMethod] = useState('GET');
  const [headers, setHeaders] = useState([{ key: '', value: '' }]);
  const [body, setBody] = useState('');
  const [authType, setAuthType] = useState('None');
  const [authValue, setAuthValue] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [methodMenuOpen, setMethodMenuOpen] = useState(false);
  const [authTypeMenuOpen, setAuthTypeMenuOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const [codeGenOpen, setCodeGenOpen] = useState(false); // Collapsed by default
  const [selectedLanguage, setSelectedLanguage] = useState('javascript-fetch');
  const [codeLangMenuOpen, setCodeLangMenuOpen] = useState(false);
  const methodMenuRef = useRef(null);
  const authTypeMenuRef = useRef(null);
  const historyMenuRef = useRef(null);
  const codeLangMenuRef = useRef(null);

  const filteredFeatures = FEATURES.filter(f => 
    f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.category.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const categories = [...new Set(FEATURES.map(f => f.category))];

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '' }]);
  };

  const updateHeader = (index, field, value) => {
    const newHeaders = [...headers];
    newHeaders[index][field] = value;
    setHeaders(newHeaders);
  };

  const removeHeader = (index) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  // Keyboard navigation for dropdowns
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (methodMenuOpen) setMethodMenuOpen(false);
        if (authTypeMenuOpen) setAuthTypeMenuOpen(false);
        if (historyOpen) setHistoryOpen(false);
        if (codeLangMenuOpen) setCodeLangMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [methodMenuOpen, authTypeMenuOpen, historyOpen]);

  // Load history from storage on mount
  useEffect(() => {
    const loadHistory = async () => {
      try {
        if (chrome?.storage?.local) {
          const result = await chrome.storage.local.get(['restApiHistory']);
          if (result.restApiHistory) {
            setHistory(result.restApiHistory);
          }
        } else {
          // Fallback to localStorage for development
          const saved = localStorage.getItem('restApiHistory');
          if (saved) {
            setHistory(JSON.parse(saved));
          }
        }
      } catch (err) {
        console.error('Failed to load history:', err);
      }
    };
    loadHistory();
  }, []);

  // Save history to storage
  const saveHistory = async (requestData) => {
    try {
      const newHistoryItem = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        ...requestData,
      };
      const updatedHistory = [newHistoryItem, ...history].slice(0, 100); // Keep last 100
      setHistory(updatedHistory);
      
      if (chrome?.storage?.local) {
        await chrome.storage.local.set({ restApiHistory: updatedHistory });
      } else {
        localStorage.setItem('restApiHistory', JSON.stringify(updatedHistory));
      }
    } catch (err) {
      console.error('Failed to save history:', err);
    }
  };

  // Load request from history
  const loadFromHistory = (historyItem) => {
    setUrl(historyItem.url || '');
    setMethod(historyItem.method || 'GET');
    setHeaders(historyItem.headers || [{ key: '', value: '' }]);
    setBody(historyItem.body || '');
    setAuthType(historyItem.authType || 'None');
    setAuthValue(historyItem.authValue || '');
    setHistoryOpen(false);
  };

  // Clear history
  const clearHistory = async () => {
    try {
      setHistory([]);
      if (chrome?.storage?.local) {
        await chrome.storage.local.remove('restApiHistory');
      } else {
        localStorage.removeItem('restApiHistory');
      }
      setHistoryOpen(false);
    } catch (err) {
      console.error('Failed to clear history:', err);
    }
  };

  // Click outside detection for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (methodMenuRef.current && !methodMenuRef.current.contains(event.target)) {
        setMethodMenuOpen(false);
      }
      if (authTypeMenuRef.current && !authTypeMenuRef.current.contains(event.target)) {
        setAuthTypeMenuOpen(false);
      }
      if (historyMenuRef.current && !historyMenuRef.current.contains(event.target)) {
        setHistoryOpen(false);
      }
      if (codeLangMenuRef.current && !codeLangMenuRef.current.contains(event.target)) {
        setCodeLangMenuOpen(false);
      }
    };
    if (methodMenuOpen || authTypeMenuOpen || historyOpen || codeLangMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [methodMenuOpen, authTypeMenuOpen, historyOpen, codeLangMenuOpen]);

  const sendRequest = async () => {
    if (!url.trim()) {
      setResponse({
        error: 'Please enter a valid URL',
        ok: false,
      });
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      setResponse({
        error: 'Invalid URL format. Please enter a valid URL (e.g., https://example.com/api)',
        ok: false,
      });
      return;
    }

    setLoading(true);
    setResponse(null);

    try {
      const requestHeaders = {};
      headers.forEach(({ key, value }) => {
        if (key && value) {
          requestHeaders[key] = value;
        }
      });

      if (authType === 'Basic' && authValue) {
        requestHeaders['Authorization'] = `Basic ${btoa(authValue)}`;
      } else if (authType === 'Bearer Token' && authValue) {
        requestHeaders['Authorization'] = `Bearer ${authValue}`;
      }

      const startTime = Date.now();
      const fetchOptions = {
        method,
        headers: requestHeaders,
      };

      if (method !== 'GET' && body) {
        fetchOptions.body = body;
        if (!requestHeaders['Content-Type']) {
          requestHeaders['Content-Type'] = 'application/json';
        }
      }

      const res = await fetch(url, fetchOptions);
      const endTime = Date.now();
      const latency = endTime - startTime;

      let responseBody;
      const contentType = res.headers.get('content-type');
      try {
        if (contentType && contentType.includes('application/json')) {
          responseBody = await res.json();
        } else {
          responseBody = await res.text();
        }
      } catch (parseError) {
        // If response body parsing fails, use text
        try {
          responseBody = await res.text();
        } catch {
          responseBody = `[Unable to parse response body: ${parseError.message}]`;
        }
      }

      const responseData = {
        status: res.status,
        statusText: res.statusText,
        headers: Object.fromEntries(res.headers.entries()),
        body: responseBody,
        latency,
        ok: res.ok,
      };
      setResponse(responseData);

      // Save to history
      await saveHistory({
        url,
        method,
        headers,
        body,
        authType,
        authValue,
        response: responseData,
      });
    } catch (error) {
      // Provide user-friendly error messages
      let errorMessage = error.message || 'Request failed';
      if (error.message?.includes('Failed to fetch')) {
        errorMessage = 'Network error: Unable to connect to the server. Check the URL and your internet connection.';
      } else if (error.message?.includes('CORS')) {
        errorMessage = 'CORS error: The server is blocking cross-origin requests.';
      } else if (error.message?.includes('timeout')) {
        errorMessage = 'Request timeout: The server took too long to respond.';
      }
      
      const errorResponse = {
        error: errorMessage,
        ok: false,
      };
      setResponse(errorResponse);

      // Save error to history too
      await saveHistory({
        url,
        method,
        headers,
        body,
        authType,
        authValue,
        response: errorResponse,
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredHistory = history.filter(item => {
    if (!historySearchQuery) return true;
    const query = historySearchQuery.toLowerCase();
    return (
      item.url?.toLowerCase().includes(query) ||
      item.method?.toLowerCase().includes(query) ||
      JSON.stringify(item.headers || {}).toLowerCase().includes(query)
    );
  });

  // Generate code snippet
  const generateCode = (lang) => {
    const requestHeaders = {};
    headers.forEach(({ key, value }) => {
      if (key && value) {
        requestHeaders[key] = value;
      }
    });

    if (authType === 'Basic' && authValue) {
      requestHeaders['Authorization'] = `Basic ${btoa(authValue)}`;
    } else if (authType === 'Bearer Token' && authValue) {
      requestHeaders['Authorization'] = `Bearer ${authValue}`;
    }

    const headersStr = JSON.stringify(requestHeaders, null, 2);
    const bodyStr = body ? (body.trim().startsWith('{') ? body : JSON.stringify(body)) : '';

    switch (lang) {
      case 'javascript-fetch':
        return `fetch('${url}', {
  method: '${method}',
  headers: ${headersStr}${body && method !== 'GET' ? `,\n  body: ${bodyStr}` : ''}
})
  .then(response => response.json())
  .then(data => console.log(data))
  .catch(error => console.error('Error:', error));`;

      case 'javascript-axios':
        return `import axios from 'axios';

axios.${method.toLowerCase()}('${url}', ${body && method !== 'GET' ? bodyStr : '{}'}, {
  headers: ${headersStr}
})
  .then(response => console.log(response.data))
  .catch(error => console.error('Error:', error));`;

      case 'python-requests':
        return `import requests

headers = ${headersStr.replace(/\n/g, '\n  ').replace(/{/, '{').replace(/}/, '}  ')}
${body && method !== 'GET' ? `data = ${bodyStr.replace(/\n/g, '\n  ')}\n` : ''}
response = requests.${method.toLowerCase()}('${url}'${body && method !== 'GET' ? ', json=data' : ''}, headers=headers)
print(response.json())`;

      case 'curl':
        const curlHeaders = Object.entries(requestHeaders)
          .map(([k, v]) => `-H "${k}: ${v}"`)
          .join(' \\\n  ');
        const curlBody = body && method !== 'GET' ? `-d '${bodyStr.replace(/'/g, "'\\''")}'` : '';
        return `curl -X ${method} '${url}' \\
  ${curlHeaders}${curlBody ? ` \\\n  ${curlBody}` : ''}`;

      case 'go':
        const goHeaders = Object.entries(requestHeaders)
          .map(([k, v]) => `  req.Header.Set("${k}", "${v}")`)
          .join('\n');
        const goBody = body && method !== 'GET' 
          ? `  data := ${bodyStr.replace(/\n/g, '\n  ')}\n  jsonData, _ := json.Marshal(data)\n  `
          : '';
        return `package main

import (
  "bytes"
  "encoding/json"
  "net/http"
  "fmt"
)

func main() {
  url := "${url}"
${goBody}  req, _ := http.NewRequest("${method}", url, ${body && method !== 'GET' ? 'bytes.NewBuffer(jsonData)' : 'nil'})
${goHeaders}
  
  client := &http.Client{}
  resp, _ := client.Do(req)
  defer resp.Body.Close()
  
  var result map[string]interface{}
  json.NewDecoder(resp.Body).Decode(&result)
  fmt.Println(result)
}`;

      default:
        return '';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'text-gray-500';
    if (status >= 200 && status < 300) return 'text-success';
    if (status >= 300 && status < 400) return 'text-warning';
    return 'text-error';
  };

  const getStatusIcon = (status) => {
    if (!status) return AlertCircle;
    if (status >= 200 && status < 300) return CheckCircle2;
    if (status >= 300 && status < 400) return AlertCircle;
    return XCircle;
  };

  const isRouterMode = typeof window !== 'undefined' && window.__ROUTER_MODE__;
  
  const content = (
    <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 flex flex-col" style={{ width: '100%', minWidth: 0 }}>
        <div className="flex-1 flex flex-col">
          <div className="mx-auto max-w-[1600px] w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-10">
            <div className="space-y-4 sm:space-y-6">
          {/* Professional Header with Border */}
          <header className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl shadow-sm px-4 sm:px-6 lg:px-8 py-4 sm:py-6 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="space-y-1 sm:space-y-2">
                <div className="flex items-center gap-3">
                  <Network className="h-8 w-8 text-gray-900 dark:text-white" />
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">REST API Tester</h1>
                </div>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  Test REST APIs with custom headers, authentication, and request bodies. All requests are made from your browser.
                </p>
              </div>
            </div>
          </header>

          <div className="space-y-4 sm:space-y-6">
            {/* Request Panel */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="h-1.5 sm:h-2 w-1.5 sm:w-2 rounded-full flex-shrink-0 bg-gray-400 dark:bg-gray-500" />
                    <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">Request</h2>
                  </div>
                </div>
                <div className="p-4 sm:p-6 space-y-4">
                  <div>
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => setHistoryOpen(!historyOpen)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-all focus:outline-none border-[0.5px] border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-700 hover:shadow"
                        tabIndex={0}
                        title="Request History"
                      >
                        <History className="h-3.5 w-3.5 dark:text-white" />
                        <span className="hidden sm:inline">History</span>
                        {history.length > 0 && (
                          <span className="px-1.5 py-0.5 text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded">
                            {history.length}
                          </span>
                        )}
                      </button>

                      {/* History Dropdown */}
                      {historyOpen && (
                        <div className="relative" ref={historyMenuRef}>
                          <div className="fixed inset-0 z-40" onClick={() => setHistoryOpen(false)} />
                          <div className="absolute left-0 top-full mt-2 w-full sm:w-96 max-w-[calc(100vw-2rem)] border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-2xl z-50 max-h-[600px] flex flex-col">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                              <div className="flex items-center gap-2">
                                <History className="h-4 w-4 text-gray-600 dark:text-white" />
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Request History</h3>
                                {history.length > 0 && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded">
                                    {history.length}
                                  </span>
                                )}
                              </div>
                              {history.length > 0 && (
                                <button
                                  onClick={clearHistory}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 dark:text-white hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                                  title="Clear all history"
                                >
                                  <Trash2 className="h-3.5 w-3.5 dark:text-white" />
                                  <span className="hidden sm:inline">Clear</span>
                                </button>
                              )}
                            </div>
                            {history.length === 0 ? (
                              <div className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                                <History className="h-10 w-10 mx-auto mb-3 text-gray-300 dark:text-gray-500" />
                                <p className="text-sm dark:text-white">No request history yet</p>
                                <p className="text-xs mt-1 dark:text-gray-400">Your requests will appear here</p>
                              </div>
                            ) : (
                              <>
                                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                                  <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                                    <input
                                      type="text"
                                      value={historySearchQuery}
                                      onChange={(e) => setHistorySearchQuery(e.target.value)}
                                      placeholder="Search history..."
                                      className="w-full pl-8 pr-3 py-1.5 text-xs border-[0.5px] border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg focus:outline-none focus:border-sky-400/60 dark:focus:border-sky-500/60"
                                    />
                                  </div>
                                </div>
                                <div className="overflow-y-auto flex-1">
                                  {filteredHistory.length === 0 ? (
                                    <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-sm dark:text-white">
                                      No results found
                                    </div>
                                  ) : (
                                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                      {filteredHistory.map((item) => (
                                        <button
                                          key={item.id}
                                          onClick={() => loadFromHistory(item)}
                                          className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                                        >
                                          <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-2 mb-1">
                                                <span className="px-2 py-0.5 text-xs font-semibold rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-white">
                                                  {item.method || 'GET'}
                                                </span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400 truncate flex-1">
                                                  {new Date(item.timestamp).toLocaleString()}
                                                </span>
                                              </div>
                                              <div className="text-xs text-gray-700 dark:text-white truncate mb-1">
                                                {item.url || 'No URL'}
                                              </div>
                                              {item.response && (
                                                <div className="flex items-center gap-2">
                                                  {item.response.status ? (
                                                    <>
                                                      <span className={clsx(
                                                        'text-xs font-medium',
                                                        item.response.status >= 200 && item.response.status < 300
                                                          ? 'text-green-600 dark:text-green-400'
                                                          : item.response.status >= 300 && item.response.status < 400
                                                          ? 'text-yellow-600 dark:text-yellow-400'
                                                          : 'text-red-600 dark:text-red-400'
                                                      )}>
                                                        {item.response.status}
                                                      </span>
                                                      {item.response.latency && (
                                                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-0.5">
                                                          <Clock className="h-3 w-3 dark:text-gray-400" />
                                                          {item.response.latency}ms
                                                        </span>
                                                      )}
                                                    </>
                                                  ) : (
                                                    <span className="text-xs text-red-600 dark:text-red-400">Error</span>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="relative" ref={methodMenuRef}>
                        <button
                          type="button"
                          onClick={() => setMethodMenuOpen(!methodMenuOpen)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setMethodMenuOpen(!methodMenuOpen);
                            }
                          }}
                          className="rounded-lg border-[0.5px] border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-white focus:outline-none focus:border-sky-400/60 dark:focus:border-sky-500/60 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center justify-between min-w-[100px]"
                          tabIndex={0}
                          aria-expanded={methodMenuOpen}
                        >
                          <span>{method}</span>
                          <ChevronDown className={clsx('h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2', methodMenuOpen && 'rotate-180')} />
                        </button>
                        {methodMenuOpen && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setMethodMenuOpen(false)} />
                            <div className="absolute left-0 top-full mt-1 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-lg z-50 overflow-hidden min-w-[100px]">
                              {HTTP_METHODS.map((m) => (
                                <button
                                  key={m}
                                  onClick={() => {
                                    setMethod(m);
                                    setMethodMenuOpen(false);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setMethod(m);
                                      setMethodMenuOpen(false);
                                    }
                                  }}
                                  className={clsx(
                                    'w-full px-3 py-2 text-left text-sm transition-colors',
                                    method === m 
                                      ? 'bg-gray-100 dark:bg-gray-700 font-medium text-gray-900 dark:text-white' 
                                      : 'text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                                  )}
                                  tabIndex={0}
                                >
                                  {m}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      <input
                        type="text"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://your-instance.service-now.com/api/now/table/incident"
                        className="flex-1 rounded-lg border-[0.5px] border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 sm:px-4 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-sky-400/60 dark:focus:border-sky-500/60"
                        tabIndex={0}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-white">Headers</label>
                      <button
                        onClick={addHeader}
                        className="text-xs text-gray-600 dark:text-white hover:text-gray-900 dark:hover:text-gray-300 transition active:scale-95"
                        tabIndex={0}
                      >
                        + Add Header
                      </button>
                    </div>
                    <div className="space-y-2">
                      {headers.map((header, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={header.key}
                            onChange={(e) => updateHeader(index, 'key', e.target.value)}
                            placeholder="Header name"
                            className="flex-1 rounded-lg border-[0.5px] border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-sky-400/60 dark:focus:border-sky-500/60"
                            tabIndex={0}
                          />
                          <input
                            type="text"
                            value={header.value}
                            onChange={(e) => updateHeader(index, 'value', e.target.value)}
                            placeholder="Header value"
                            className="flex-1 rounded-lg border-[0.5px] border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-sky-400/60 dark:focus:border-sky-500/60"
                            tabIndex={0}
                          />
                          {headers.length > 1 && (
                            <button
                              onClick={() => removeHeader(index)}
                              className="rounded-lg px-2 text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600 transition active:scale-95"
                              tabIndex={0}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-white mb-2 block">Authentication</label>
                    <div className="space-y-2">
                      <div className="relative" ref={authTypeMenuRef}>
                        <button
                          type="button"
                          onClick={() => setAuthTypeMenuOpen(!authTypeMenuOpen)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setAuthTypeMenuOpen(!authTypeMenuOpen);
                            }
                          }}
                          className="w-full text-left rounded-lg border-[0.5px] border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-white focus:outline-none focus:border-sky-400/60 dark:focus:border-sky-500/60 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center justify-between"
                          tabIndex={0}
                          aria-expanded={authTypeMenuOpen}
                        >
                          <span>{authType}</span>
                          <ChevronDown className={clsx('h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 flex-shrink-0', authTypeMenuOpen && 'rotate-180')} />
                        </button>
                        {authTypeMenuOpen && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setAuthTypeMenuOpen(false)} />
                            <div className="absolute left-0 right-0 top-full mt-1 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-lg z-50 overflow-hidden">
                              {AUTH_TYPES.map((type) => (
                                <button
                                  key={type}
                                  onClick={() => {
                                    setAuthType(type);
                                    setAuthTypeMenuOpen(false);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setAuthType(type);
                                      setAuthTypeMenuOpen(false);
                                    }
                                  }}
                                  className={clsx(
                                    'w-full px-3 py-2 text-left text-sm transition-colors',
                                    authType === type 
                                      ? 'bg-gray-100 dark:bg-gray-700 font-medium text-gray-900 dark:text-white' 
                                      : 'text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                                  )}
                                  tabIndex={0}
                                >
                                  {type}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      {authType !== 'None' && (
                        <input
                          type={authType === 'Basic' ? 'text' : 'password'}
                          value={authValue}
                          onChange={(e) => setAuthValue(e.target.value)}
                          placeholder={authType === 'Basic' ? 'username:password' : 'Token or key'}
                          className="w-full rounded-lg border-[0.5px] border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 sm:px-4 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-sky-400/60 dark:focus:border-sky-500/60"
                          tabIndex={0}
                        />
                      )}
                    </div>
                  </div>

                  {method !== 'GET' && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-white mb-2 block">Request Body</label>
                      <textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder='{"key": "value"}'
                        className="w-full min-h-[120px] rounded-lg border-[0.5px] border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 sm:px-4 py-2 sm:py-3 font-mono text-sm leading-relaxed text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-sky-400/60 dark:focus:border-sky-500/60 focus:bg-sky-50/30 dark:focus:bg-sky-900/20 resize-none"
                        tabIndex={0}
                      />
                    </div>
                  )}

                  <button
                    onClick={sendRequest}
                    disabled={loading || !url.trim()}
                    className="w-full rounded-lg px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold text-white bg-sky-500 hover:bg-sky-600 transition-all disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-500 dark:disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-300 dark:disabled:hover:bg-gray-600 focus:outline-none shadow-sm hover:shadow flex items-center justify-center gap-2"
                    tabIndex={0}
                  >
                    <Play className="h-4 w-4 dark:text-white" />
                    {loading ? 'Sending...' : 'Send Request'}
                  </button>

                  {/* Code Generation Section */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                    <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg">
                      <button
                        onClick={() => setCodeGenOpen(!codeGenOpen)}
                        className="flex items-center gap-2 flex-1"
                        tabIndex={0}
                      >
                        <FileCode className="h-4 w-4 text-gray-600 dark:text-white" />
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">Generate Code</span>
                        <ChevronDown className={clsx('h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ml-auto', codeGenOpen && 'rotate-180')} />
                      </button>
                      {codeGenOpen && (
                        <button
                          onClick={() => copyText(generateCode(selectedLanguage))}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold transition-all focus:outline-none border-[0.5px] border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-700 hover:shadow ml-2"
                          tabIndex={0}
                          title="Copy Code"
                        >
                          <Copy className="h-3.5 w-3.5 dark:text-white" />
                          <span className="hidden sm:inline">Copy</span>
                        </button>
                      )}
                    </div>
                    {codeGenOpen && (
                      <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-3">
                        <div className="relative" ref={codeLangMenuRef}>
                          <button
                            type="button"
                            onClick={() => setCodeLangMenuOpen(!codeLangMenuOpen)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setCodeLangMenuOpen(!codeLangMenuOpen);
                              }
                            }}
                            className="w-full text-left rounded-lg border-[0.5px] border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-white focus:outline-none focus:border-sky-400/60 dark:focus:border-sky-500/60 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center justify-between"
                            tabIndex={0}
                            aria-expanded={codeLangMenuOpen}
                          >
                            <span>
                              {selectedLanguage === 'javascript-fetch' && 'JavaScript (fetch)'}
                              {selectedLanguage === 'javascript-axios' && 'JavaScript (axios)'}
                              {selectedLanguage === 'python-requests' && 'Python (requests)'}
                              {selectedLanguage === 'curl' && 'cURL'}
                              {selectedLanguage === 'go' && 'Go'}
                            </span>
                            <ChevronDown className={clsx('h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 flex-shrink-0', codeLangMenuOpen && 'rotate-180')} />
                          </button>
                          {codeLangMenuOpen && (
                            <>
                              <div className="fixed inset-0 z-40" onClick={() => setCodeLangMenuOpen(false)} />
                              <div className="absolute left-0 right-0 top-full mt-1 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-lg z-50 overflow-hidden">
                                {[
                                  { value: 'javascript-fetch', label: 'JavaScript (fetch)' },
                                  { value: 'javascript-axios', label: 'JavaScript (axios)' },
                                  { value: 'python-requests', label: 'Python (requests)' },
                                  { value: 'curl', label: 'cURL' },
                                  { value: 'go', label: 'Go' },
                                ].map((lang) => (
                                  <button
                                    key={lang.value}
                                    onClick={() => {
                                      setSelectedLanguage(lang.value);
                                      setCodeLangMenuOpen(false);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSelectedLanguage(lang.value);
                                        setCodeLangMenuOpen(false);
                                      }
                                    }}
                                    className={clsx(
                                      'w-full px-3 py-2 text-left text-sm transition-colors',
                                      selectedLanguage === lang.value 
                                        ? 'bg-gray-100 dark:bg-gray-700 font-medium text-gray-900 dark:text-white' 
                                        : 'text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                                    )}
                                    tabIndex={0}
                                  >
                                    {lang.label}
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 p-3 sm:p-4 max-h-96 overflow-auto">
                          <pre className="text-sm font-mono text-gray-900 dark:text-white whitespace-pre-wrap leading-relaxed">
                            <code>{generateCode(selectedLanguage)}</code>
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Response Panel */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="h-1.5 sm:h-2 w-1.5 sm:w-2 rounded-full flex-shrink-0 bg-gray-400 dark:bg-gray-500" />
                    <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">Response</h2>
                    {response && !loading && (
                      <>
                        {(() => {
                          const StatusIcon = getStatusIcon(response.status);
                          return (
                            <StatusIcon className={clsx('h-4 w-4 sm:h-5 sm:w-5', getStatusColor(response.status))} />
                          );
                        })()}
                        <div className="flex items-center gap-2 sm:gap-3">
                          <span className={clsx('text-sm sm:text-lg font-semibold', getStatusColor(response.status))}>
                            {response.status || 'Error'}
                          </span>
                          {response.statusText && (
                            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:inline">{response.statusText}</span>
                          )}
                          {response.latency && (
                            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                              <Clock className="h-3 w-3 dark:text-gray-400" />
                              <span>{response.latency}ms</span>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  {!response && !loading && (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <Network className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-500" />
                      <p className="text-sm dark:text-white">Send a request to see the response here</p>
                    </div>
                  )}
                  {loading && (
                    <div className="text-center py-12">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">Sending request...</p>
                    </div>
                  )}
                  {response && !loading && (
                    <div className="space-y-4">
                      {response.error ? (
                        <div className="rounded-lg border border-error/20 dark:border-error/40 bg-error/5 dark:bg-error/20 p-4 text-sm text-error dark:text-red-400">
                          {response.error}
                        </div>
                      ) : (
                        <>
                          {response.headers && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h3 className="text-xs font-semibold text-gray-700 dark:text-white">Response Headers</h3>
                                <button
                                  onClick={() => copyText(JSON.stringify(response.headers, null, 2))}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-all focus:outline-none border-[0.5px] border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-700 hover:shadow"
                                  tabIndex={0}
                                >
                                  <Copy className="h-3.5 w-3.5 dark:text-white" />
                                  Copy
                                </button>
                              </div>
                              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 p-3 sm:p-4 max-h-96 overflow-auto">
                                <pre className="text-sm font-mono text-gray-700 dark:text-white leading-relaxed">
                                  {JSON.stringify(response.headers, null, 2)}
                                </pre>
                              </div>
                            </div>
                          )}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-xs font-semibold text-gray-700 dark:text-white">Response Body</h3>
                              <button
                                onClick={() => copyText(typeof response.body === 'string' ? response.body : JSON.stringify(response.body, null, 2))}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-all focus:outline-none border-[0.5px] border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-700 hover:shadow"
                                tabIndex={0}
                              >
                                <Copy className="h-3.5 w-3.5 dark:text-white" />
                                Copy
                              </button>
                            </div>
                            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 p-3 sm:p-4 max-h-96 overflow-auto">
                              <pre className="text-sm font-mono text-gray-700 dark:text-white leading-relaxed">
                                {typeof response.body === 'string' ? response.body : JSON.stringify(response.body, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
  );
  
  if (isRouterMode) {
    return content;
  }
  
  return (
    <div className="flex h-screen bg-white dark:bg-gray-900 overflow-hidden">
      <Navigation currentPageId="rest" sidebarOpen={sidebarOpen} onSidebarToggle={setSidebarOpen} />
      {content}
    </div>
  );
};

// Export component for Router, render directly for extension
const RestTesterComponent = () => <RestTester />;
export default RestTesterComponent;

// Render directly if running as standalone (extension mode)
if (typeof window !== 'undefined' && document.getElementById('root') && !window.__ROUTER_MODE__) {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(<RestTester />);
}
